import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { createVignetteTexture, shadeColor } from './materialTextures.jsx';
import { loadPbrMaps, disposePbrMaps, ensureUv2, getPbrLayout, PBR_SETS } from './pbrTextures.jsx';
import { DEFAULT_ROOM_LIGHT_PCT, clampLightPct } from './roomLighting.jsx';
import { buildSurfaceGeometry, spaceEdges, innerRect, piecesFrame, edgeLoops, ringFlatGeometry, ringPrismGeometry, segOnWall, ringShapeGeometry, ringSolidGeometry, maxInset } from './spacePieces.jsx';
import { isTrapPiece } from '../elements/spaces.jsx';

// «المان» سقفِ سه‌بعدی. سقف دو انتخابِ کاملاً مستقل دارد:
//
//   ۱) مدل (style.type) — «شکل»ِ سقف و تزئیناتش:
//      • ساده (flat)          — فقط خودِ سطح
//      • کاذب/کناف (tile)      — شبکه‌ی پروفیل‌های واقعیِ سه‌بعدی بینِ پنل‌ها (روی هر جنسی)
//      • نورِ مخفی (cove)      — شیارِ تیره + نوارِ نورانی نزدیکِ دیوارها + درخششِ نرم روی سقف
//      • چندسطحی (twoTone)     — یک حاشیه‌ی «واقعاً» پایین‌تر (هندسه‌ی سه‌بعدی) دورتادورِ سقف
//      • گچ‌بری (plasterFrame) — دو قابِ برجسته‌ی نازک (هندسه‌ی سه‌بعدی) نزدیکِ لبه‌ها
//      • چندسطحی + نورِ مخفی (multiCove) — دو پله‌ی واقعی دورتادور، با نوارِ نورانیِ دیده‌شونده روی
//        لبه‌ی هر پله و تابشِ نرمِ نور روی سطحِ بالاترِ آن
//
//   ۲) جنس (style.material) — «سطح»ِ سقف:
//      • رنگ (paint — بافتِ واقعیِ سطحِ رنگ‌شده)، کشسان/براق (glossy — یک‌دست و صیقلی)
//      • عکس‌های واقعیِ PBR: گچِ خاکستری، گچِ بافت‌دارِ سفید، چوبِ روشن، چوبِ تیره
//
// هر مدلی با هر جنسی ترکیب می‌شود (مثلاً «نورِ مخفی» + «گچِ بافت‌دارِ سفید»)، چون تزئیناتِ مدل دیگر
// داخلِ بافتِ سطح کشیده نمی‌شوند؛ همه هندسه‌ی سه‌بعدیِ جدا هستند (هیچ بافتِ کدنویسی‌شده‌ای نیست).
//
// نسخه‌های قبلی این دو را در یک لیستِ «نوع» قاطی داشتند (wood/glossy/plasterPbr/woodPbr هم «نوع»
// بودند) — resolveCeilingStyle پایین، سقف‌های ذخیره‌شده‌ی قدیمی را خودکار به قالبِ جدید تبدیل می‌کند.
//
// برخلافِ کف، برای هر اتاق یک رنگِ پیش‌فرضِ جدا از پالت در نظر گرفته نمی‌شود — در واقعیت تقریباً
// همیشه کلِ سقفِ خانه یک رنگ (سفید/کرم) است

const DEFAULT_CEILING_COLOR = '#f2efe6';
const DEFAULT_CEILING_ACCENT = '#d9d3c4';

export const DEFAULT_CEILING_STYLE = {
  type: 'flat',
  material: 'paint',
  color: DEFAULT_CEILING_COLOR,
  accent: DEFAULT_CEILING_ACCENT,
  tileCm: 60,
  // شدتِ روشناییِ کلِ اتاق (۱۰ تا ۱۰۰٪) — به سقف ربطی ندارد، ولی چون تنظیمش از پنلِ سقف است همین‌جا
  // کنارِ بقیه‌ی تنظیماتِ اتاق ذخیره می‌شود؛ ۷۰٪ = نورِ پیش‌فرضِ صحنه. نگاه کنید به roomLighting.jsx
  lightPct: DEFAULT_ROOM_LIGHT_PCT,
};

export const CEILING_MODELS = ['flat', 'tile', 'cove', 'twoTone', 'plasterFrame', 'multiCove'];
export const CEILING_MATERIALS = ['paint', 'glossy', 'plasterGray', 'plasterWhite', 'woodPine', 'woodDark'];

// جنس → ستِ عکسِ واقعی در pbrTextures.jsx (paint/glossy عکس ندارند)
export const PBR_CEILING_MATERIAL_TO_SET = {
  // «رنگ» هم رنگِ واقعی است (بافتِ سطحِ رنگ‌شده)؛ فقط «کشسان/براق» عمداً یک‌دست و صیقلی می‌ماند
  paint: 'paintedPlaster',
  plasterGray: 'concreteGray',
  plasterWhite: 'plasterWhite',
  woodPine: 'woodPine',
  woodDark: 'woodDark',
};

// «نوع»های قدیمی که در واقع جنس بودند → { مدل، جنس }ِ معادل در قالبِ جدید
const LEGACY_TYPES = {
  wood: { type: 'flat', material: 'woodPine' },
  woodPbr: { type: 'flat', material: 'woodPine' },
  plasterPbr: { type: 'flat', material: 'plasterGray' },
  glossy: { type: 'flat', material: 'glossy' },
};

// rect.ceilingStyle ممکن است ناقص، خالی یا در قالبِ قدیمی باشد — همیشه یک شیءِ کامل در قالبِ جدید
// برمی‌گرداند
export function resolveCeilingStyle(rect) {
  const s = rect?.ceilingStyle;
  const legacy = LEGACY_TYPES[s?.type];
  const rawType = legacy ? legacy.type : s?.type;
  const type = CEILING_MODELS.includes(rawType) ? rawType : DEFAULT_CEILING_STYLE.type;
  const rawMaterial = s?.material || legacy?.material;
  const material = CEILING_MATERIALS.includes(rawMaterial) ? rawMaterial : DEFAULT_CEILING_STYLE.material;
  return {
    type,
    material,
    color: s?.color || DEFAULT_CEILING_STYLE.color,
    accent: s?.accent || DEFAULT_CEILING_STYLE.accent,
    tileCm: s?.tileCm || DEFAULT_CEILING_STYLE.tileCm,
    lightPct: clampLightPct(s?.lightPct ?? DEFAULT_CEILING_STYLE.lightPct),
  };
}

// مدل‌هایی که «اندازه‌ی الگو» دارند (فقط شبکه‌ی پنل‌های کاذب) — ceilingStyleBox.jsx برای تبِ الگو
export const PATTERNED_CEILING_TYPES = ['tile'];

// مدل‌هایی که از «رنگِ فرعی» استفاده می‌کنند: درزِ پنل، رنگِ نور، رنگِ حاشیه‌ی پایین‌تر، رنگِ گچ‌بری
export const ACCENT_CEILING_TYPES = ['tile', 'cove', 'twoTone', 'plasterFrame', 'multiCove'];

// ظاهرِ سطح برای جنس‌های بدونِ عکس — metalness همیشه صفر (سقف از خیلی نزدیک دیده می‌شود و هر
// بازتابِ فلزی آن‌جا تیرگیِ ناخواسته می‌سازد)؛ «کشسان» فقط با roughnessِ خیلی پایین براق می‌شود
const PLAIN_FINISH = {
  paint: { roughness: 0.9, envMapIntensity: 0.3 },
  glossy: { roughness: 0.08, envMapIntensity: 0.9 },
};

// یک سطحِ سقف (صفحه‌ی اصلی، یا یک جعبه‌ی حاشیه‌ی چندسطحی) با جنسِ انتخاب‌شده. هر سطح نقشه‌های PBRِ
// خودش را با تکرارِ متناسب با ابعادِ خودش می‌گیرد (همان الگوی DoorSurface/FrameBar)؛ عکس‌ها به لطفِ
// کشِ pbrTextures فقط یک بار لود/آپلود می‌شوند
function useCeilingMaterialProps(material, color, sizeX, sizeY) {
  const setKey = PBR_CEILING_MATERIAL_TO_SET[material];
  const pbrSet = setKey ? PBR_SETS[setKey] : null;
  const { repeatX, repeatY, rotate } = getPbrLayout(pbrSet, sizeX, sizeY);
  const rX = Math.max(0.25, repeatX);
  const rY = Math.max(0.25, repeatY);

  const pbrMaps = useMemo(
    () => (pbrSet ? loadPbrMaps(pbrSet, rX, rY, { rotate }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setKey, rX, rY, rotate]
  );
  useEffect(() => () => disposePbrMaps(pbrMaps), [pbrMaps]);

  if (pbrSet) {
    return {
      key: `pbr-${setKey}`,
      color,
      map: pbrMaps?.map || null,
      normalMap: pbrMaps?.normalMap || null,
      roughnessMap: pbrMaps?.roughnessMap || null,
      aoMap: pbrMaps?.aoMap || null,
      roughness: 1,
      metalness: 0,
      envMapIntensity: 0.5,
    };
  }
  const finish = PLAIN_FINISH[material] || PLAIN_FINISH.paint;
  // همه‌ی propها همیشه صراحتاً داده می‌شوند (map=null و ...) و key با جنس عوض می‌شود — همان درسِ باگِ
  // «سقفِ سیاه»: اگر یک متریال با propهای ناقص/متفاوت دوباره‌استفاده شود، نتیجه سیاه می‌شود
  return {
    key: `plain-${material}`,
    color,
    map: null,
    normalMap: null,
    roughnessMap: null,
    aoMap: null,
    roughness: finish.roughness,
    metalness: 0,
    envMapIntensity: finish.envMapIntensity,
  };
}

function CeilingBox({ position, size, material, color, rotationY = 0 }) {
  // size = [x, ضخامت, z]؛ رویه‌ی زیرینِ جعبه (که از پایین دیده می‌شود) ابعادِ x×z دارد
  const props = useCeilingMaterialProps(material, color, size[0], size[2]);
  const { key, ...matProps } = props;
  return (
    <mesh position={position} rotation={[0, rotationY, 0]}>
      <boxGeometry ref={ensureUv2} args={size} />
      <meshStandardMaterial key={key} {...matProps} />
    </mesh>
  );
}

// شبکه‌ی سقفِ کاذب/کناف به‌صورتِ «هندسه‌ی واقعی»: پروفیل‌های باریکِ T‌شکل (نوارهای نازکِ برجسته) که
// پنل‌ها را از هم جدا می‌کنند — درست مثلِ سقفِ کاذبِ واقعی، و روی هر جنسی می‌نشیند. خطوط از مرکزِ اتاق
// چیده می‌شوند (مثلِ اجرای واقعی) تا پنل‌های لبه متقارن باشند
const GRID_BAR_WIDTH_CM = 1.6;
const GRID_BAR_DEPTH_CM = 1;
const GRID_MAX_LINES_PER_AXIS = 60; // سقفِ ایمنی برای اتاق‌های خیلی بزرگ

function gridPositions(start, end, step) {
  const center = (start + end) / 2;
  const out = [];
  const half = (end - start) / 2;
  const n = Math.min(GRID_MAX_LINES_PER_AXIS, Math.floor(half / step));
  // اگر تعدادِ پنل‌ها زوج باشد یک خط دقیقاً از مرکز می‌گذرد؛ ساده‌ترین حالتِ متقارن همین است
  out.push(center);
  for (let i = 1; i <= n; i += 1) {
    const d = i * step;
    if (d < half - 1) {
      out.push(center - d, center + d);
    }
  }
  return out;
}

function CeilingGrid({ rect, y, tileCm, color }) {
  const width = rect.x2 - rect.x1;
  const depth = rect.y2 - rect.y1;
  const xs = gridPositions(rect.x1, rect.x2, tileCm);
  const zs = gridPositions(rect.y1, rect.y2, tileCm);
  const centerX = (rect.x1 + rect.x2) / 2;
  const centerZ = (rect.y1 + rect.y2) / 2;
  return (
    <group>
      {xs.map((x) => (
        <mesh key={`x${x}`} position={[x, y, centerZ]}>
          <boxGeometry args={[GRID_BAR_WIDTH_CM, GRID_BAR_DEPTH_CM, depth]} />
          <meshStandardMaterial color={color} roughness={0.5} metalness={0} />
        </mesh>
      ))}
      {zs.map((z) => (
        <mesh key={`z${z}`} position={[centerX, y, z]}>
          <boxGeometry args={[width, GRID_BAR_DEPTH_CM, GRID_BAR_WIDTH_CM]} />
          <meshStandardMaterial color={color} roughness={0.5} metalness={0} />
        </mesh>
      ))}
    </group>
  );
}

// یک قابِ مستطیلیِ برجسته (چهار نوارِ نازک) — برای گچ‌بری
function RaisedFrame({ x1, x2, z1, z2, y, line, thick, color }) {
  const w = x2 - x1;
  const d = z2 - z1;
  const cx = (x1 + x2) / 2;
  const cz = (z1 + z2) / 2;
  if (w <= line * 2 || d <= line * 2) return null;
  const mat = <meshStandardMaterial color={color} roughness={0.75} metalness={0} />;
  return (
    <group>
      <mesh position={[cx, y, z1 + line / 2]}>
        <boxGeometry args={[w, thick, line]} />
        {mat}
      </mesh>
      <mesh position={[cx, y, z2 - line / 2]}>
        <boxGeometry args={[w, thick, line]} />
        {mat}
      </mesh>
      <mesh position={[x1 + line / 2, y, cz]}>
        <boxGeometry args={[line, thick, d - line * 2]} />
        {mat}
      </mesh>
      <mesh position={[x2 - line / 2, y, cz]}>
        <boxGeometry args={[line, thick, d - line * 2]} />
        {mat}
      </mesh>
    </group>
  );
}

// ابعادِ حاشیه‌ی «چندسطحی» — پهنایش نسبتی از کوچک‌ترین ضلعِ اتاق (همان ۱۴٪ِ نسخه‌ی قبلی)، ولی
// محدود به بازه‌ی واقع‌گرایانه؛ و واقعاً چند سانت پایین‌تر از سطحِ اصلی
const TWO_TONE_BAND_RATIO = 0.14;
const TWO_TONE_BAND_MIN_CM = 25;
const TWO_TONE_BAND_MAX_CM = 80;
const TWO_TONE_DROP_CM = 8;
// گچ‌بری: دو قاب با فاصله‌ی ۸٪ و ۱۱٪ از لبه (همان نسبت‌های نسخه‌ی قبلی)
const MOLDING_OUTER_RATIO = 0.08;
const MOLDING_INNER_RATIO = 0.11;
const MOLDING_LINE_CM = 3;
const MOLDING_THICK_CM = 2;

// فاصله‌ی نوارِ نورِ مخفی از دیوارها (سانتی‌متر) — حسِ یک کانالِ نورِ فرورفته/عقب‌نشسته را می‌دهد، نه
// این‌که دقیقاً روی خودِ دیوار بچسبد
const COVE_INSET_CM = 18;
// ابعادِ خودِ نوار (ضخامت/عمق) — نازک و کم‌ارتفاع، فقط برای حسِ بصریِ یک منبعِ نورِ باریک
const COVE_STRIP_SIZE = 3;
// عرضِ «شیارِ» تیره‌ی درست کنارِ دیوار، قبل از خودِ نوارِ نورانی — همان فاصله‌ای که در واقعیت بینِ
// دیوار و لبه‌ی کاذبِ نورِ مخفی سایه‌ی طبیعی می‌افتد؛ بدونِ این، نوار مثلِ یک جعبه‌ی رنگیِ چسبیده به
// دیوار به‌نظر می‌رسید، نه یک کانالِ واقعاً فرورفته
const COVE_GROOVE_CM = 8;

// حاشیه/قرنیزِ نازک دورتادورِ محلِ اتصالِ سقف به دیوار — برای همه‌ی انواع به‌جز «نورِ مخفی» (که خودش
// شیار/نوارِ مخصوصِ خودش را دارد) اضافه می‌شود، تا سقف به‌جای قطعِ ناگهانی/شناور، یک خطِ پایانیِ
// تمیز و متصل به دیوار داشته باشد — دقیقاً همان چیزی که هر سقفِ واقعی (حتیِ ساده‌ترین‌ها هم) دارد
const TRIM_WIDTH_CM = 5;
const TRIM_THICKNESS_CM = 1.4;

// وینیتِ سایه‌یِ نرم دورِ لبه — برای همه‌ی انواع (به‌جز نورِ مخفی که به‌جایش وینیتِ گرمِ نورانی دارد)
// اضافه می‌شود؛ افتِ طبیعیِ نورِ محیطی در گوشه‌های هر اتاقِ واقعی را شبیه‌سازی می‌کند تا سقف، به‌جای
// یک سطحِ کاملاً یک‌دست/تختِ غیرطبیعی، کمی عمق/حجم بصری داشته باشد
const SHADOW_VIGNETTE_TINT = '#140f09';
const SHADOW_VIGNETTE_STRENGTH = 0.3;
// برای نورِ مخفی: به‌جای تیره‌شدنِ گوشه‌ها، سقف نزدیکِ دیوارها با رنگِ خودِ نوار «شسته» می‌شود — دقیقاً
// حسِ واقعیِ نورِ غیرمستقیمی که از کانالِ نورِ مخفی به سقف/دیوارِ اطراف می‌تابد
const COVE_GLOW_STRENGTH = 0.5;

// ---------------------------------------------------------------------------------------------------
// «چندسطحی + نورِ مخفی» — مثلِ سقفِ کاذبِ کنافِ واقعی: دو پله‌ی دورتادور (پله‌ی بیرونی پایین‌تر، پله‌ی
// داخلی کمی بالاتر، وسط = خودِ سقف). روی لبه‌ی عمودیِ هر پله (درست زیرِ سطحِ بالاتر) یک نوارِ LED
// نورانی کشیده می‌شود که «خودش دیده می‌شود»، و نورش روی سطحِ بالاتر نرم پخش می‌شود (درخششِ جمعی با
// رنگِ نور، از لبه به داخل محو).
const MULTI_DROP_OUTER_CM = 24; // پله‌ی بیرونی: ۲۴ سانت پایین‌تر از سقف
const MULTI_DROP_INNER_CM = 12; // پله‌ی داخلی: ۱۲ سانت پایین‌تر از سقف
const MULTI_OUTER_RATIO = 0.12;
const MULTI_INNER_RATIO = 0.1;
const MULTI_MIN_CENTER_CM = 80; // وسطِ سقف حداقل این‌قدر باز بماند
const LED_HEIGHT_CM = 1.8;
const LED_DEPTH_CM = 1.2;
const LED_BELOW_SURFACE_CM = 2.2; // فاصله‌ی مرکزِ نوار تا سطحِ بالاتر
const GLOW_OPACITY = 0.45;
const GLOW_MAX_WIDTH_CM = 55;

let glowGradient = null;
// گرادیانِ مشترکِ درخشش: v=۰ (لبه‌ی پله) = پرنور، تا v=۱ کاملاً محو
function getGlowGradient() {
  if (glowGradient) return glowGradient;
  const h = 128;
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(4, h);
  for (let row = 0; row < h; row += 1) {
    const v = 1 - row / (h - 1); // row 0 = بالای بوم = v=1
    const c = Math.round(Math.pow(1 - v, 1.8) * 255);
    for (let x = 0; x < 4; x += 1) {
      const i = (row * 4 + x) * 4;
      img.data[i] = c;
      img.data[i + 1] = c;
      img.data[i + 2] = c;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  glowGradient = new THREE.CanvasTexture(canvas);
  return glowGradient;
}

const noRaycast = () => null;

// یک حلقه‌ی پله (چهار جعبه‌ی دورتادورِ مستطیلِ [x1,x2]×[z1,z2] با پهنای band)، زیرش در ارتفاعِ yBottom
function CeilingRing({ x1, x2, z1, z2, band, yBottom, yTop, material, color }) {
  const w = x2 - x1;
  const d = z2 - z1;
  const thick = yTop - yBottom;
  if (band <= 0 || thick <= 0) return null;
  const cx = (x1 + x2) / 2;
  const cz = (z1 + z2) / 2;
  const y = (yBottom + yTop) / 2;
  const sideD = Math.max(1, d - band * 2);
  return (
    <group>
      <CeilingBox position={[cx, y, z1 + band / 2]} size={[w, thick, band]} material={material} color={color} />
      <CeilingBox position={[cx, y, z2 - band / 2]} size={[w, thick, band]} material={material} color={color} />
      <CeilingBox position={[x1 + band / 2, y, cz]} size={[band, thick, sideD]} material={material} color={color} />
      <CeilingBox position={[x2 - band / 2, y, cz]} size={[band, thick, sideD]} material={material} color={color} />
    </group>
  );
}

// نوارِ LED روی چهار لبه‌ی داخلیِ یک پله (مستطیلِ [x1,x2]×[z1,z2] = دهانه‌ی داخلِ پله) + درخششِ نرم روی
// سطحِ بالاترِ آن (در ارتفاعِ surfaceY)
function CoveStep({ x1, x2, z1, z2, surfaceY, color, glowWidth }) {
  const w = x2 - x1;
  const d = z2 - z1;
  if (w <= 2 || d <= 2) return null;
  const cx = (x1 + x2) / 2;
  const cz = (z1 + z2) / 2;
  const ledY = surfaceY - LED_BELOW_SURFACE_CM;
  const o = LED_DEPTH_CM / 2; // نوار کمی از لبه‌ی پله جلو می‌آید تا از زیر دیده شود
  const g = Math.max(4, Math.min(glowWidth, w / 2, d / 2));
  const glowY = surfaceY - 0.4;
  const flat = Math.PI / 2; // صفحه‌ی افقیِ رو به پایین
  const led = <meshBasicMaterial color={color} toneMapped={false} />;
  const glow = (
    <meshBasicMaterial
      color={color}
      alphaMap={getGlowGradient()}
      transparent
      opacity={GLOW_OPACITY}
      depthWrite={false}
      toneMapped={false}
      blending={THREE.AdditiveBlending}
    />
  );
  return (
    <group>
      <mesh position={[cx, ledY, z1 + o]} raycast={noRaycast}>
        <boxGeometry args={[w, LED_HEIGHT_CM, LED_DEPTH_CM]} />
        {led}
      </mesh>
      <mesh position={[cx, ledY, z2 - o]} raycast={noRaycast}>
        <boxGeometry args={[w, LED_HEIGHT_CM, LED_DEPTH_CM]} />
        {led}
      </mesh>
      <mesh position={[x1 + o, ledY, cz]} raycast={noRaycast}>
        <boxGeometry args={[LED_DEPTH_CM, LED_HEIGHT_CM, d]} />
        {led}
      </mesh>
      <mesh position={[x2 - o, ledY, cz]} raycast={noRaycast}>
        <boxGeometry args={[LED_DEPTH_CM, LED_HEIGHT_CM, d]} />
        {led}
      </mesh>
      {/* درخشش: چرخشِ درون‌صفحه‌ای (rotation[2]) تعیین می‌کند سمتِ پرنورِ گرادیان رو به کدام لبه باشد */}
      <mesh position={[cx, glowY, z1 + g / 2]} rotation={[flat, 0, 0]} raycast={noRaycast} renderOrder={3}>
        <planeGeometry args={[w, g]} />
        {glow}
      </mesh>
      <mesh position={[cx, glowY, z2 - g / 2]} rotation={[flat, 0, Math.PI]} raycast={noRaycast} renderOrder={3}>
        <planeGeometry args={[w, g]} />
        {glow}
      </mesh>
      <mesh position={[x1 + g / 2, glowY, cz]} rotation={[flat, 0, -Math.PI / 2]} raycast={noRaycast} renderOrder={3}>
        <planeGeometry args={[d, g]} />
        {glow}
      </mesh>
      <mesh position={[x2 - g / 2, glowY, cz]} rotation={[flat, 0, Math.PI / 2]} raycast={noRaycast} renderOrder={3}>
        <planeGeometry args={[d, g]} />
        {glow}
      </mesh>
    </group>
  );
}

// پهنای دو پله متناسب با اتاق، طوری که وسطِ سقف همیشه حداقل MULTI_MIN_CENTER_CM باز بماند
function multiCoveBands(width, depth) {
  const m = Math.min(width, depth);
  let outer = Math.min(60, Math.max(35, m * MULTI_OUTER_RATIO));
  let inner = Math.min(45, Math.max(25, m * MULTI_INNER_RATIO));
  const maxTotal = Math.max(0, (m - MULTI_MIN_CENTER_CM) / 2);
  if (outer + inner > maxTotal) {
    const k = maxTotal / (outer + inner);
    outer *= k;
    inner *= k;
  }
  return { outer, inner };
}

function MultiCoveCeiling({ rect, height, style }) {
  const width = rect.x2 - rect.x1;
  const depth = rect.y2 - rect.y1;
  const { outer, inner } = multiCoveBands(width, depth);
  if (outer < 5) return null;
  const x1 = rect.x1;
  const x2 = rect.x2;
  const z1 = rect.y1;
  const z2 = rect.y2;
  const H = height;
  const bi = outer + inner;
  return (
    <group>
      {/* پله‌ی بیرونی (پایین‌ترین) و پله‌ی داخلی — هر دو با جنس و رنگِ اصلیِ سقف */}
      <CeilingRing x1={x1} x2={x2} z1={z1} z2={z2} band={outer} yBottom={H - MULTI_DROP_OUTER_CM} yTop={H} material={style.material} color={style.color} />
      <CeilingRing
        x1={x1 + outer}
        x2={x2 - outer}
        z1={z1 + outer}
        z2={z2 - outer}
        band={inner}
        yBottom={H - MULTI_DROP_INNER_CM}
        yTop={H}
        material={style.material}
        color={style.color}
      />
      {/* نورِ پله‌ی اول: روی لبه‌ی پله‌ی بیرونی، می‌تابد روی زیرِ پله‌ی داخلی */}
      <CoveStep
        x1={x1 + outer}
        x2={x2 - outer}
        z1={z1 + outer}
        z2={z2 - outer}
        surfaceY={H - MULTI_DROP_INNER_CM}
        color={style.accent}
        glowWidth={Math.min(GLOW_MAX_WIDTH_CM, inner)}
      />
      {/* نورِ پله‌ی دوم: روی لبه‌ی پله‌ی داخلی، می‌تابد روی خودِ سقفِ وسط */}
      <CoveStep
        x1={x1 + bi}
        x2={x2 - bi}
        z1={z1 + bi}
        z2={z2 - bi}
        surfaceY={H}
        color={style.accent}
        glowWidth={GLOW_MAX_WIDTH_CM}
      />
    </group>
  );
}

// یک صفحه‌ی سقف به ازای هر اتاق (roomGroupRect) — رو به پایین (نرمالش رو به کف)، در ارتفاعِ بالای
// دیوار. فقط از دیدِ اول‌شخص رندر می‌شود، پس همیشه از زیر دیده می‌شود (FrontSide کافی است).
// ساختار: ۱) سطحِ اصلی با «جنس»  ۲) تزئیناتِ «مدل» روی/زیرِ آن  ۳) وینیت  ۴) قرنیز یا کانالِ نور
function CeilingPanel({ rect, height, style }) {
  const width = Math.max(1, rect.x2 - rect.x1);
  const depth = Math.max(1, rect.y2 - rect.y1);
  const centerX = (rect.x1 + rect.x2) / 2;
  const centerY = (rect.y1 + rect.y2) / 2;

  const s = style || DEFAULT_CEILING_STYLE;
  const isCove = s.type === 'cove';
  const isTile = s.type === 'tile';
  const isTwoTone = s.type === 'twoTone';
  const isMolding = s.type === 'plasterFrame';
  const isMultiCove = s.type === 'multiCove';

  // ۱) سطحِ اصلی — جنس
  const surface = useCeilingMaterialProps(s.material, s.color, width, depth);
  const { key: surfaceKey, ...surfaceProps } = surface;

  // ۲-الف) شبکه‌ی سقفِ کاذب — پروفیل‌های واقعیِ سه‌بعدی، فقط برای مدلِ «کاذب/کناف»
  const tileCm = Math.max(20, s.tileCm || DEFAULT_CEILING_STYLE.tileCm);

  // ۳) وینیت — برای نورِ مخفی درخششِ گرم (blending جمعی)، برای بقیه سایه‌ی نرمِ گوشه‌ها
  const vignetteTexture = useMemo(
    () =>
      isCove
        ? createVignetteTexture(s.accent, COVE_GLOW_STRENGTH)
        : createVignetteTexture(SHADOW_VIGNETTE_TINT, SHADOW_VIGNETTE_STRENGTH),
    [isCove, s.accent]
  );
  useEffect(() => () => vignetteTexture?.dispose(), [vignetteTexture]);

  const coveStripW = Math.max(1, width - COVE_INSET_CM * 2);
  const coveStripD = Math.max(1, depth - COVE_INSET_CM * 2);
  const grooveW = width;
  const grooveD = depth;
  const trimW = width;
  const trimD = depth;
  // قرنیز کمی روشن‌تر از رنگِ سطح؛ برای چوب/گچِ عکس‌دار هم همان رنگِ پایه مبناست
  const trimColor = shadeColor(s.color, 14);

  // ۲-ب) چندسطحی — حاشیه‌ی واقعاً پایین‌ترِ دورتادور، با همان جنسِ سطح (رنگش = رنگِ فرعی)
  const band = Math.min(
    TWO_TONE_BAND_MAX_CM,
    Math.max(TWO_TONE_BAND_MIN_CM, Math.min(width, depth) * TWO_TONE_BAND_RATIO),
    Math.min(width, depth) / 3
  );
  const bandY = height - TWO_TONE_DROP_CM / 2;

  // ۲-ج) گچ‌بری — دو قابِ برجسته‌ی نازک، کمی زیرِ سطح
  const moldY = height - MOLDING_THICK_CM / 2 - 0.3;
  const outerX = width * MOLDING_OUTER_RATIO;
  const outerZ = depth * MOLDING_OUTER_RATIO;
  const innerX = width * MOLDING_INNER_RATIO;
  const innerZ = depth * MOLDING_INNER_RATIO;

  return (
    <group>
      <mesh position={[centerX, height, centerY]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry ref={ensureUv2} args={[width, depth]} />
        <meshStandardMaterial key={surfaceKey} {...surfaceProps} />
      </mesh>

      {isTile && (
        <CeilingGrid rect={rect} y={height - GRID_BAR_DEPTH_CM / 2 - 0.2} tileCm={tileCm} color={s.accent} />
      )}

      {isTwoTone && (
        <group>
          <CeilingBox
            position={[centerX, bandY, rect.y1 + band / 2]}
            size={[width, TWO_TONE_DROP_CM, band]}
            material={s.material}
            color={s.accent}
          />
          <CeilingBox
            position={[centerX, bandY, rect.y2 - band / 2]}
            size={[width, TWO_TONE_DROP_CM, band]}
            material={s.material}
            color={s.accent}
          />
          <CeilingBox
            position={[rect.x1 + band / 2, bandY, centerY]}
            size={[band, TWO_TONE_DROP_CM, Math.max(1, depth - band * 2)]}
            material={s.material}
            color={s.accent}
          />
          <CeilingBox
            position={[rect.x2 - band / 2, bandY, centerY]}
            size={[band, TWO_TONE_DROP_CM, Math.max(1, depth - band * 2)]}
            material={s.material}
            color={s.accent}
          />
        </group>
      )}

      {isMolding && (
        <group>
          <RaisedFrame
            x1={rect.x1 + outerX}
            x2={rect.x2 - outerX}
            z1={rect.y1 + outerZ}
            z2={rect.y2 - outerZ}
            y={moldY}
            line={MOLDING_LINE_CM}
            thick={MOLDING_THICK_CM}
            color={s.accent}
          />
          <RaisedFrame
            x1={rect.x1 + innerX}
            x2={rect.x2 - innerX}
            z1={rect.y1 + innerZ}
            z2={rect.y2 - innerZ}
            y={moldY}
            line={MOLDING_LINE_CM * 0.7}
            thick={MOLDING_THICK_CM * 0.8}
            color={s.accent}
          />
        </group>
      )}

      {isMultiCove && <MultiCoveCeiling rect={rect} height={height} style={s} />}

      {/* وینیتِ سایه/نور — صفحه‌ی مجزای کمی پایین‌تر (برای جلوگیری از z-fighting). برای چندسطحی
          زیرِ حاشیه‌ی پایین‌تر قرار می‌گیرد تا رویش سایه بیفتد، نه این‌که پشتش پنهان شود. «چندسطحی + نورِ
          مخفی» وینیت ندارد — نورِ خودِ پله‌ها جایش را می‌گیرد، و صفحه‌ی تختِ وینیت عمقِ پله‌ها را خراب می‌کرد */}
      {!isMultiCove && (
      <mesh
        position={[centerX, (isTwoTone ? height - TWO_TONE_DROP_CM : height) - 0.8, centerY]}
        rotation={[Math.PI / 2, 0, 0]}
      >
        <planeGeometry args={[width, depth]} />
        <meshBasicMaterial
          key={isCove ? 'cove-glow' : 'shadow'}
          map={vignetteTexture}
          transparent
          depthWrite={false}
          toneMapped={!isCove}
          blending={isCove ? THREE.AdditiveBlending : THREE.NormalBlending}
        />
      </mesh>
      )}

      {isCove ? (
        <group>
          {/* شیارِ تیره‌ی کنارِ دیوار + نوارِ نورانی (meshBasicMaterial، مستقل از نورپردازی؛
              toneMapped={false} تا زیرِ ACES کدر نشود) */}
          <mesh position={[centerX, height - 1, rect.y1 + COVE_GROOVE_CM / 2]}>
            <boxGeometry args={[grooveW, 1.2, COVE_GROOVE_CM]} />
            <meshStandardMaterial color="#1c1712" roughness={0.95} metalness={0} />
          </mesh>
          <mesh position={[centerX, height - 1, rect.y2 - COVE_GROOVE_CM / 2]}>
            <boxGeometry args={[grooveW, 1.2, COVE_GROOVE_CM]} />
            <meshStandardMaterial color="#1c1712" roughness={0.95} metalness={0} />
          </mesh>
          <mesh position={[rect.x1 + COVE_GROOVE_CM / 2, height - 1, centerY]}>
            <boxGeometry args={[COVE_GROOVE_CM, 1.2, grooveD]} />
            <meshStandardMaterial color="#1c1712" roughness={0.95} metalness={0} />
          </mesh>
          <mesh position={[rect.x2 - COVE_GROOVE_CM / 2, height - 1, centerY]}>
            <boxGeometry args={[COVE_GROOVE_CM, 1.2, grooveD]} />
            <meshStandardMaterial color="#1c1712" roughness={0.95} metalness={0} />
          </mesh>
          <mesh position={[centerX, height - COVE_STRIP_SIZE, rect.y1 + COVE_INSET_CM]}>
            <boxGeometry args={[coveStripW, 1.6, COVE_STRIP_SIZE]} />
            <meshBasicMaterial color={s.accent} toneMapped={false} />
          </mesh>
          <mesh position={[centerX, height - COVE_STRIP_SIZE, rect.y2 - COVE_INSET_CM]}>
            <boxGeometry args={[coveStripW, 1.6, COVE_STRIP_SIZE]} />
            <meshBasicMaterial color={s.accent} toneMapped={false} />
          </mesh>
          <mesh position={[rect.x1 + COVE_INSET_CM, height - COVE_STRIP_SIZE, centerY]}>
            <boxGeometry args={[COVE_STRIP_SIZE, 1.6, coveStripD]} />
            <meshBasicMaterial color={s.accent} toneMapped={false} />
          </mesh>
          <mesh position={[rect.x2 - COVE_INSET_CM, height - COVE_STRIP_SIZE, centerY]}>
            <boxGeometry args={[COVE_STRIP_SIZE, 1.6, coveStripD]} />
            <meshBasicMaterial color={s.accent} toneMapped={false} />
          </mesh>
        </group>
      ) : (
        // قرنیزِ نازکِ دورتادورِ اتصالِ سقف به دیوار — برای چندسطحی زیرِ حاشیه‌ی پایین‌تر
        <group>
          {(() => {
            const trimTop = isTwoTone
              ? height - TWO_TONE_DROP_CM
              : isMultiCove
                ? height - MULTI_DROP_OUTER_CM
                : height;
            const ty = trimTop - TRIM_THICKNESS_CM / 2;
            return (
              <>
                <mesh position={[centerX, ty, rect.y1 + TRIM_WIDTH_CM / 2]}>
                  <boxGeometry args={[trimW, TRIM_THICKNESS_CM, TRIM_WIDTH_CM]} />
                  <meshStandardMaterial color={trimColor} roughness={0.7} metalness={0} />
                </mesh>
                <mesh position={[centerX, ty, rect.y2 - TRIM_WIDTH_CM / 2]}>
                  <boxGeometry args={[trimW, TRIM_THICKNESS_CM, TRIM_WIDTH_CM]} />
                  <meshStandardMaterial color={trimColor} roughness={0.7} metalness={0} />
                </mesh>
                <mesh position={[rect.x1 + TRIM_WIDTH_CM / 2, ty, centerY]}>
                  <boxGeometry args={[TRIM_WIDTH_CM, TRIM_THICKNESS_CM, trimD]} />
                  <meshStandardMaterial color={trimColor} roughness={0.7} metalness={0} />
                </mesh>
                <mesh position={[rect.x2 - TRIM_WIDTH_CM / 2, ty, centerY]}>
                  <boxGeometry args={[TRIM_WIDTH_CM, TRIM_THICKNESS_CM, trimD]} />
                  <meshStandardMaterial color={trimColor} roughness={0.7} metalness={0} />
                </mesh>
              </>
            );
          })()}
        </group>
      )}
    </group>
  );
}


// ---------------------------------------------------------------------------------------------------
// سقفِ فضای چندتکه — مثلاً اتاقِ بزرگی که اتاقِ کوچکی داخلش کشیده شده، یا فضای L‌شکلِ دیوارکشیِ دستی. سطح
// برای هر تکه جدا، ولی همه‌ی تزئیناتِ لبه (کانالِ نورِ مخفی، چندسطحی، حاشیه‌ی دورنگ، قرنیز) دورِ «مرزِ واقعیِ»
// فضا کشیده می‌شوند — یعنی دورِ دیوارهای اتاقِ داخلی هم، نه فقط دورِ مستطیلِ بیرونی

function inPieces(pieces, x, z) {
  return pieces.some((p) => x > p.x1 && x < p.x2 && z > p.y1 && z < p.y2);
}

// لبه‌های مرزی: { axis: 'x' (لبه‌ی افقی، z ثابت) | 'z' (x ثابت), c, a, b, n (جهتِ داخل: ±۱), convexA, convexB }
export function boundaryEdges(pieces) {
  const raw = [];
  pieces.forEach((p, i) => {
    const others = pieces.filter((_, j) => j !== i);
    const sides = [
      { axis: 'x', c: p.y1, a: p.x1, b: p.x2, n: 1 },
      { axis: 'x', c: p.y2, a: p.x1, b: p.x2, n: -1 },
      { axis: 'z', c: p.x1, a: p.y1, b: p.y2, n: 1 },
      { axis: 'z', c: p.x2, a: p.y1, b: p.y2, n: -1 },
    ];
    sides.forEach((e) => {
      // بازه را با لبه‌های تکه‌های دیگر خرد کن و هر خرده را جدا بسنج
      const cutsAt = new Set([e.a, e.b]);
      others.forEach((o) => {
        const lo = e.axis === 'x' ? o.x1 : o.y1;
        const hi = e.axis === 'x' ? o.x2 : o.y2;
        if (lo > e.a && lo < e.b) cutsAt.add(lo);
        if (hi > e.a && hi < e.b) cutsAt.add(hi);
      });
      const ts = [...cutsAt].sort((u, v) => u - v);
      for (let k = 0; k < ts.length - 1; k += 1) {
        const m = (ts[k] + ts[k + 1]) / 2;
        const outC = e.c - e.n * 1;
        const outside = e.axis === 'x' ? !inPieces(others, m, outC) : !inPieces(others, outC, m);
        if (outside && ts[k + 1] - ts[k] > 0.5) raw.push({ ...e, a: ts[k], b: ts[k + 1] });
      }
    });
  });
  // خرده‌های پشتِ‌سرِهم روی یک خط → یک لبه
  raw.sort((u, v) => (u.axis + u.n + u.c).localeCompare(v.axis + v.n + v.c) || u.a - v.a);
  const merged = [];
  raw.forEach((e) => {
    const last = merged[merged.length - 1];
    if (last && last.axis === e.axis && last.n === e.n && Math.abs(last.c - e.c) < 0.01 && Math.abs(last.b - e.a) < 0.01) last.b = e.b;
    else merged.push({ ...e });
  });
  // گوشه‌ها: محدب (سرِ لبه به دیوارِ عمود می‌رسد) یا مقعر (فضا ادامه دارد)
  const E = 2;
  merged.forEach((e) => {
    const inC = e.c + e.n * E;
    const test = (t) => (e.axis === 'x' ? inPieces(pieces, t, inC) : inPieces(pieces, inC, t));
    e.convexA = !test(e.a - E);
    e.convexB = !test(e.b + E);
  });
  return merged;
}

// سطحِ کلِ سقفِ یک فضای چندتکه/مورب: یک هندسه، یک دست بافت، UVِ پیوسته روی همه‌ی تکه‌ها
function SpaceSurface({ pieces, height, s }) {
  const frame = useMemo(() => piecesFrame(pieces), [pieces]);
  const geometry = useMemo(() => buildSurfaceGeometry(pieces, frame, height, false), [pieces, frame, height]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  const { key, ...props } = useCeilingMaterialProps(s.material, s.color, frame.x2 - frame.x1, frame.y2 - frame.y1);
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial key={key} {...props} />
    </mesh>
  );
}

// سقفِ فضای چندتکه/مورب: سطح یک‌تکه (SpaceSurface) و همه‌ی تزئیناتِ «مدل» به‌صورتِ قاب‌های فارسی‌بُر دورِ مرزِ واقعیِ
// فضا (spacePieces.edgeLoops) — همان چیزهایی که اتاقِ ساده دارد: نورِ مخفی (شیار، نوارِ LED، درخششِ نزدیک و هاله‌ی
// گرمِ دورتادور)، چندسطحی (دو پله + دو ردیف نور)، حاشیه‌ی دورنگ، گچ‌بری، کاذب، قرنیز و سایه‌ی نرمِ لبه‌ها
// شبکه‌ی سقفِ کاذب برای هر شکلی: یک شبکه‌ی پیوسته (از وسطِ کادرِ فضا، مثلِ اتاقِ ساده) که هر خطش فقط همان‌جا که
// داخلِ فضاست کشیده می‌شود — نه یک شبکه‌ی جدا برای هر تکه
function lineSpans(pieces, axis, v) {
  const spans = [];
  pieces.forEach((q) => {
    const xl0 = q.xl0 !== undefined ? q.xl0 : q.x1;
    const xl1 = q.xl0 !== undefined ? q.xl1 : q.x1;
    const xr0 = q.xl0 !== undefined ? q.xr0 : q.x2;
    const xr1 = q.xl0 !== undefined ? q.xr1 : q.x2;
    if (axis === 'z') {
      // خطِ افقی (z ثابت): بازه‌ی x
      if (v < q.y1 || v > q.y2) return;
      const t = q.y2 > q.y1 ? (v - q.y1) / (q.y2 - q.y1) : 0;
      spans.push([xl0 + (xl1 - xl0) * t, xr0 + (xr1 - xr0) * t]);
      return;
    }
    // خطِ عمودی (x ثابت): بازه‌ی t که xl(t) ≤ v ≤ xr(t)
    let lo = 0;
    let hi = 1;
    [
      [xl0 - v, xl1 - v],
      [v - xr0, v - xr1],
    ].forEach(([a, b]) => {
      // a + (b − a)·t ≤ 0
      const d = b - a;
      if (Math.abs(d) < 1e-9) {
        if (a > 0) hi = -1;
      } else if (d > 0) hi = Math.min(hi, -a / d);
      else lo = Math.max(lo, -a / d);
    });
    if (hi > lo) spans.push([q.y1 + (q.y2 - q.y1) * lo, q.y1 + (q.y2 - q.y1) * hi]);
  });
  spans.sort((p1, p2) => p1[0] - p2[0]);
  const out = [];
  spans.forEach((sp) => {
    const last = out[out.length - 1];
    if (last && sp[0] <= last[1] + 1) last[1] = Math.max(last[1], sp[1]);
    else out.push([...sp]);
  });
  return out.filter((sp) => sp[1] - sp[0] > 2);
}

function ShapeGrid({ pieces, frame, y, tileCm, color }) {
  const bars = useMemo(() => {
    const out = [];
    gridPositions(frame.x1, frame.x2, tileCm).forEach((x) =>
      lineSpans(pieces, 'x', x).forEach(([a, b]) => out.push({ pos: [x, y, (a + b) / 2], size: [GRID_BAR_WIDTH_CM, GRID_BAR_DEPTH_CM, b - a] }))
    );
    gridPositions(frame.y1, frame.y2, tileCm).forEach((z) =>
      lineSpans(pieces, 'z', z).forEach(([a, b]) => out.push({ pos: [(a + b) / 2, y, z], size: [b - a, GRID_BAR_DEPTH_CM, GRID_BAR_WIDTH_CM] }))
    );
    return out;
  }, [pieces, frame, y, tileCm]);
  return (
    <group>
      {bars.map((b, i) => (
        <mesh key={i} position={b.pos}>
          <boxGeometry args={b.size} />
          <meshStandardMaterial color={color} roughness={0.5} metalness={0} />
        </mesh>
      ))}
    </group>
  );
}

function RingMesh({ geometry, renderOrder, children }) {
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <mesh geometry={geometry} raycast={noRaycast} renderOrder={renderOrder}>
      {children}
    </mesh>
  );
}

function MultiPieceCeiling({ rect, height, style, walls }) {
  const s = style || DEFAULT_CEILING_STYLE;
  const pieces = rect.rects;
  const H = height;
  const frame = useMemo(() => piecesFrame(pieces), [pieces]);
  const loops = useMemo(() => edgeLoops(spaceEdges(pieces)), [pieces]);
  const fw = Math.max(1, frame.x2 - frame.x1);
  const fd = Math.max(1, frame.y2 - frame.y1);
  // «پهنای مفید»ِ فضا = دو برابرِ بیشترین فاصله‌ی امن به داخل (برای مستطیل دقیقاً همان ضلعِ کوتاه) — اندازه‌ی
  // نوارها درست مثلِ اتاقِ ساده از همین می‌آید؛ قبلاً از بزرگ‌ترین مستطیلِ داخلی بود و در فضای مورب/L خیلی باریک می‌شد
  const limRaw = useMemo(() => maxInset(loops, pieces), [loops, pieces]);
  const minSide = Math.max(60, limRaw * 2);
  const tileCm = Math.max(20, s.tileCm || DEFAULT_CEILING_STYLE.tileCm);
  const trimColor = shadeColor(s.color, 14);

  const { key: mainKey, ...mainProps } = useCeilingMaterialProps(s.material, s.color, fw, fd);
  const { key: accKey, ...accProps } = useCeilingMaterialProps(s.material, s.accent, fw, fd);

  const isCove = s.type === 'cove';
  const isMultiCove = s.type === 'multiCove';
  const isTwoTone = s.type === 'twoTone';
  const isMolding = s.type === 'plasterFrame';

  const parts = useMemo(() => {
    const out = [];
    // فضای کوچک/باریک: هیچ قابی از «بیشترین فاصله‌ی امن» جلوتر نمی‌رود (وگرنه برعکس می‌شد و از دیوار بیرون می‌زد)
    const lim = limRaw * 0.92;
    const add = (kind, geometry, extra = {}) => geometry && out.push({ kind, geometry, ...extra });
    // قاب‌های پهن با عقب‌نشینیِ درستِ چندضلعی (گوشه‌ی تیز و قوس هم یک‌تکه)
    const flat = (o0, o1, y) => (o0 < lim - 1 ? ringShapeGeometry(loops, o0, Math.min(o1, lim), y, 'glow') : null);
    const prism = (o0, o1, yb, yt) => (o0 < lim - 1 ? ringSolidGeometry(loops, o0, Math.min(o1, lim), yb, yt, frame) : null);
    if (isCove) {
      add('dark', prism(0, COVE_GROOVE_CM, H - 1.6, H - 0.4));
      add('led', prism(COVE_INSET_CM - COVE_STRIP_SIZE / 2, COVE_INSET_CM + COVE_STRIP_SIZE / 2, H - COVE_STRIP_SIZE - 0.8, H - COVE_STRIP_SIZE + 0.8));
      add('glow', flat(COVE_INSET_CM, COVE_INSET_CM + Math.min(GLOW_MAX_WIDTH_CM, minSide / 2), H - 0.8), { opacity: GLOW_OPACITY });
      // هاله‌ی گرمِ پهن (جای وینیتِ نورانیِ اتاقِ ساده)
      add('glow', flat(0, Math.min(150, minSide / 2), H - 0.9), { opacity: COVE_GLOW_STRENGTH * 0.55 });
    } else if (isMultiCove) {
      let { outer, inner: inw } = multiCoveBands(minSide, minSide);
      // جا نیست؟ هر دو پله با هم باریک‌تر (نه این‌که یکی بریده شود)
      const room = lim - 20;
      if (outer + inw > room && room > 10) {
        const k = room / (outer + inw);
        outer *= k;
        inw *= k;
      }
      if (outer >= 5) {
        const bi = outer + inw;
        add('main', prism(0, outer, H - MULTI_DROP_OUTER_CM, H));
        add('main', prism(outer, bi, H - MULTI_DROP_INNER_CM, H));
        const ly1 = H - MULTI_DROP_INNER_CM - LED_BELOW_SURFACE_CM;
        add('led', prism(outer, outer + LED_DEPTH_CM, ly1 - LED_HEIGHT_CM / 2, ly1 + LED_HEIGHT_CM / 2));
        add('glow', flat(outer, outer + Math.min(GLOW_MAX_WIDTH_CM, inw), H - MULTI_DROP_INNER_CM - 0.4), { opacity: GLOW_OPACITY });
        const ly2 = H - LED_BELOW_SURFACE_CM;
        add('led', prism(bi, bi + LED_DEPTH_CM, ly2 - LED_HEIGHT_CM / 2, ly2 + LED_HEIGHT_CM / 2));
        add('glow', flat(bi, bi + Math.min(GLOW_MAX_WIDTH_CM, Math.max(10, minSide / 2 - bi)), H - 0.4), { opacity: GLOW_OPACITY });
      }
    } else if (isTwoTone) {
      const band = Math.min(TWO_TONE_BAND_MAX_CM, Math.max(TWO_TONE_BAND_MIN_CM, minSide * TWO_TONE_BAND_RATIO), minSide / 3);
      add('accent', prism(0, band, H - TWO_TONE_DROP_CM, H));
    } else if (isMolding) {
      const mo = minSide * MOLDING_OUTER_RATIO;
      const mi = minSide * MOLDING_INNER_RATIO;
      const my = H - MOLDING_THICK_CM / 2 - 0.3;
      add('mold', prism(mo, mo + MOLDING_LINE_CM, my - MOLDING_THICK_CM / 2, my + MOLDING_THICK_CM / 2));
      add('mold', prism(mi, mi + MOLDING_LINE_CM * 0.7, my - MOLDING_THICK_CM * 0.4, my + MOLDING_THICK_CM * 0.4));
    }
    if (!isCove) {
      // قرنیز و سایه‌ی کنج فقط کنارِ دیوارِ واقعی — روی «مرزِ فضا» (که دیوار ندارد) نه
      const onWall = walls ? (sg) => segOnWall(sg, walls) : null;
      const trimTop = isTwoTone ? H - TWO_TONE_DROP_CM : isMultiCove ? H - MULTI_DROP_OUTER_CM : H;
      if (lim > 1) add('trim', ringPrismGeometry(loops, 0, Math.min(TRIM_WIDTH_CM, lim), trimTop - TRIM_THICKNESS_CM, trimTop, null, onWall));
      if (!isMultiCove && lim > 1) {
        // سایه‌ی نرمِ کنارِ دیوارها (جای وینیتِ سایه‌ی اتاقِ ساده)
        add('shadow', ringFlatGeometry(loops, 0, Math.min(70, minSide * 0.3, lim), trimTop - TRIM_THICKNESS_CM - 0.3, null, onWall));
      }
    }
    return out;
  }, [loops, frame, H, minSide, limRaw, isCove, isMultiCove, isTwoTone, isMolding, walls]);

  const glowMat = (opacity) => (
    <meshBasicMaterial
      color={s.accent}
      alphaMap={getGlowGradient()}
      transparent
      opacity={opacity}
      depthWrite={false}
      toneMapped={false}
      blending={THREE.AdditiveBlending}
      side={THREE.DoubleSide}
    />
  );

  return (
    <group>
      <SpaceSurface pieces={pieces} height={H} s={s} />
      {s.type === 'tile' && <ShapeGrid pieces={pieces} frame={frame} y={H - GRID_BAR_DEPTH_CM / 2 - 0.2} tileCm={tileCm} color={s.accent} />}
      {parts.map((p, i) => {
        let mat;
        if (p.kind === 'main') mat = <meshStandardMaterial key={mainKey} {...mainProps} side={THREE.DoubleSide} />;
        else if (p.kind === 'accent') mat = <meshStandardMaterial key={accKey} {...accProps} side={THREE.DoubleSide} />;
        else if (p.kind === 'dark') mat = <meshStandardMaterial color="#1c1712" roughness={0.95} metalness={0} side={THREE.DoubleSide} />;
        else if (p.kind === 'led') mat = <meshBasicMaterial color={s.accent} toneMapped={false} side={THREE.DoubleSide} />;
        else if (p.kind === 'glow') mat = glowMat(p.opacity);
        else if (p.kind === 'trim') mat = <meshStandardMaterial color={trimColor} roughness={0.7} metalness={0} side={THREE.DoubleSide} />;
        else if (p.kind === 'mold') mat = <meshStandardMaterial color={s.accent} roughness={0.75} metalness={0} side={THREE.DoubleSide} />;
        else
          mat = (
            <meshBasicMaterial
              color={SHADOW_VIGNETTE_TINT}
              alphaMap={getGlowGradient()}
              transparent
              opacity={SHADOW_VIGNETTE_STRENGTH}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          );
        return (
          <RingMesh key={`${p.kind}${i}`} geometry={p.geometry} renderOrder={p.kind === 'glow' || p.kind === 'shadow' ? 3 : 0}>
            {mat}
          </RingMesh>
        );
      })}
    </group>
  );
}

// سقفِ کاملِ ساختمان: یک پنل به ازای هر اتاق. این کامپوننت خودش تصمیم نمی‌گیرد کِی نمایش داده شود —
// آن تصمیم (فقط در حالتِ اول‌شخص) در Create3D.jsx گرفته می‌شود، همین‌جا فقط با روم‌ها و ارتفاعِ
// مشخص‌شده رندر می‌کند
// walls: دیوارهای واقعی (برای این‌که قرنیز/سایه روی «مرزِ فضا» کشیده نشود)
function Ceiling({ roomGroupRects, height, walls = null }) {
  if (!roomGroupRects || !roomGroupRects.length) return null;
  return (
    <group>
      {roomGroupRects.map((rect) => (
        rect.rects && (rect.rects.length > 1 || isTrapPiece(rect.rects[0])) ? (
          <MultiPieceCeiling key={rect.id} rect={rect} height={height} style={resolveCeilingStyle(rect)} walls={walls} />
        ) : (
          <CeilingPanel key={rect.id} rect={rect.rects && rect.rects.length ? { ...rect, ...rect.rects[0] } : rect} height={height} style={resolveCeilingStyle(rect)} />
        )
      ))}
    </group>
  );
}

export default Ceiling;